import { useCallback, useState } from 'react';
import { useSelector } from 'react-redux';
import { RootState } from 'redux/store';
import { profileApi, useGetProfileQuery } from 'redux/Api/profileApi/ProfileApi';
import moment from 'moment';

interface ValidationErrors {
  type: string | null;
  amount: string | null;
  address: string | null;
  employee: string | null;
  max_value: string | null;
  contract_kind: string | null;
  date_start: string | null;
  date_end: string | null;
}

export const getDailyAmountLimitError = (
  amount: number,
  date_start?: string | Date | null,
  date_end?: string | Date | null,
): string | null => {
  const daysCount =
    date_start && date_end
      ? moment(date_end).startOf('day').diff(moment(date_start).startOf('day'), 'days') +
        1
      : 0;

  if (daysCount && 15000 * daysCount < amount) {
    return 'Сумма слишком большая для работы за 1 день';
  }

  return null;
};

export const useOrderValidation = () => {
  const phone = localStorage.getItem('phone');
  const { infoOrderDetail } = useSelector((state: RootState) => state.ordersDetailsSlice);
  const userInfo = useSelector(profileApi.endpoints.getInfoUser.select());
  const { data: profile } = useGetProfileQuery();
  const isTetrikaCustomer = profile?.settings?.tetrika;

  const [errors, setErrors] = useState<ValidationErrors>({
    type: null,
    amount: null,
    address: null,
    employee: null,
    max_value: null,
    contract_kind: null,
    date_start: null,
    date_end: null,
  });

  const getMinMaxPrice = (phone: string) => {
    if (phone === '9052264047' || phone === '79052264047') {
      return { min: 50, max: 200000 };
    }
    return {
      min: userInfo?.data?.order_limits?.min ?? 0,
      max: userInfo?.data?.order_limits?.max ?? 0,
    };
  };

  const validateAmount = (amount: number): string | null => {
    const { min, max } = getMinMaxPrice(phone || '');

    if (!amount) {
      return `Минимальная сумма заказа ${min} рублей`;
    }

    if (!infoOrderDetail.is_unit_based) {
      const dailyLimitError = getDailyAmountLimitError(
        amount,
        infoOrderDetail.date_start,
        infoOrderDetail.date_end,
      );
      if (dailyLimitError) {
        return dailyLimitError;
      }
    }

    if (amount < min) {
      return `Минимальная сумма заказа ${min} рублей`;
    }
    if (amount > max) {
      return `Максимальная сумма заказа ${max.toLocaleString('ru-RU')} рублей`;
    }

    return null;
  };

  const validateMaxVolume = (max: number): string | null => {
    if (!infoOrderDetail.is_unit_based) {
      return null;
    }

    const unitPrice = Number(infoOrderDetail.unit_price) || 0;
    const unitVolumeMax = Number(infoOrderDetail.unit_volume_max) || 0;

    if (!unitVolumeMax || !unitPrice) {
      return null;
    }

    if (unitPrice * unitVolumeMax > max) {
      return `Максимальный объем превышает максимальную допустимую сумму заказа ${max} ₽`;
    }

    return null;
  };

  const validateDateTime = (): { date_start: string | null; date_end: string | null } => {
    const { date_start, date_end } = infoOrderDetail;
    let dateStartError: string | null = null;
    let dateEndError: string | null = null;

    if (!date_start) {
      dateStartError = 'Укажи дату и время начала';
    } else {
      const start = moment(date_start);
      if (!start.isValid()) {
        dateStartError = 'Укажи корректную дату и время начала';
      } else if (start.isBefore(moment())) {
        dateStartError = 'Дата начала не может быть раньше текущего времени';
      } else if (start.isBefore(moment().add(1, 'hour'))) {
        dateStartError = 'Заказ можно создать не позднее чем за час до начала';
      }
    }

    if (!date_end) {
      dateEndError = 'Укажи дату и время завершения';
    } else {
      const end = moment(date_end);
      if (!end.isValid()) {
        dateEndError = 'Укажи корректную дату и время завершения';
      } else if (
        date_start &&
        moment(date_start).isValid() &&
        end.isSameOrBefore(moment(date_start))
      ) {
        dateEndError = 'Дата завершения должна быть позже даты начала';
      }
    }

    return { date_start: dateStartError, date_end: dateEndError };
  };

  const validateEmployee = useCallback(() => {
    const employee = !infoOrderDetail.worker_id ? 'Выбери исполнителя для заказа' : null;

    setErrors((prev) => ({ ...prev, employee }));
    return employee === null;
  }, [infoOrderDetail.worker_id]);

  const validateFields = () => {
    const { max } = getMinMaxPrice(phone || '');
    const dateErrors = validateDateTime();
    const newErrors: ValidationErrors = {
      amount: validateAmount(Number(infoOrderDetail.total_price)),

      address:
        infoOrderDetail.is_remote === 'Удаленно'
          ? null
          : infoOrderDetail.is_dadata_available && !infoOrderDetail.city
          ? 'Выбери адрес из списка'
          : !infoOrderDetail.is_dadata_available && !infoOrderDetail.address?.trim()
          ? 'Укажи адрес'
          : null,
      employee:
        !infoOrderDetail.worker_id && !infoOrderDetail.is_marketplace
          ? 'Выбери исполнителя для заказа'
          : null,
      type:
        !infoOrderDetail.order_type_id || Number(infoOrderDetail.order_type_id) <= 0
          ? 'Выбери категорию услуг'
          : null,
      max_value: validateMaxVolume(max),
      contract_kind:
        isTetrikaCustomer && !infoOrderDetail.contract_kind
          ? 'Выбери тип договора'
          : null,
      date_start: dateErrors.date_start,
      date_end: dateErrors.date_end,
    };
    setErrors(newErrors);
    return Object.values(newErrors).every((error) => error === null);
  };

  const validateDraftAmount = () => {
    if (infoOrderDetail.is_unit_based) {
      setErrors((prev) => ({ ...prev, amount: null }));
      return true;
    }

    const { min, max } = getMinMaxPrice(phone || '');

    const amountError = !infoOrderDetail.total_price
      ? `Минимальная сумма заказа ${min} рублей`
      : Number(infoOrderDetail.total_price) < min
      ? `Минимальная сумма заказа ${min} рублей`
      : Number(infoOrderDetail.total_price) > max
      ? `Максимальная сумма заказа ${max.toLocaleString('ru-RU')} рублей`
      : null;

    setErrors((prev) => ({ ...prev, amount: amountError }));
    return !amountError;
  };

  const clearError = useCallback((field: keyof ValidationErrors) => {
    setErrors((prev) => ({ ...prev, [field]: null }));
  }, []);

  const clearErrorForField = useCallback(
    (key: string | number) => {
      const field = String(key);
      if (field === 'amount' || field === 'unit_price' || field === 'unit_volume') {
        clearError('amount');
      }
      if (field === 'unit_volume_max' || field === 'unit_price') {
        clearError('max_value');
      }
      if (field === 'order_type_id' || field === 'order_type') clearError('type');
      if (field === 'worker_id' || field === 'employee') clearError('employee');
      if (field === 'contract_kind') clearError('contract_kind');
      if (field === 'date_start') clearError('date_start');
      if (field === 'date_end') clearError('date_end');
    },
    [clearError],
  );

  return {
    errors,
    validateFields,
    validateEmployee,
    validateDraftAmount,
    clearError,
    clearErrorForField,
  };
};
